import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import React, { useRef } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import { useTranslation } from "react-i18next";
import TextArea from "antd/es/input/TextArea";
import {
  formulateAttachmentPreview,
  readImageFileAsync,
  sanitizeDataTypeClientSide,
} from "../../../../../utils/utils";
import Actions from "../../../../../redux/actions";
import { FileAttachment } from "../../../../common/fileAttachment";
import { CameraOutlined, PlusOutlined } from "@ant-design/icons";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../constants/config/appConfig";
import { InfoButtonIcon } from "../../../../../assets/svg";
import { colors } from "../../../../../constants/colors";
import "./HelpAndSupport.scss";
import { fileImageUpload } from "../../../../../redux/services/auth/authServices";
import { createServiceProviderSupportTicketAction } from "../../../../../redux/services/common/commonServices";

const HelpAndSupport = ({ open, onClose, selectedListData }) => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  const { userSession } = useLocalStorage(appConfig.SESSION_USER_INFO);
  const { loginDetails } = useSelector((state) => state.auth);

  /* form dependencies */
  const [form] = Form.useForm();
  /* form dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [imageLoader, setImageLoader] = useState(false);

  /* module dependencies for attachment - single */
  const [singleAttachmentObj, setSingleAttachmentObj] = useState(null);
  const [singleAttachmetSelectedImageUrl, setSingleAttachmetSelectedImageUrl] =
    useState("");
  /* module dependencies for attachment - single */

  /* module dependencies for attachment */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [attachmentList, setAttachmentList] = useState([]);
  const attachmentRef = useRef(null);
  /* module dependencies for attachment */

  /* on Image changed- single attachment */
  const onImageChanged = async (e) => {
    const file = e.target.files[0];
    setSingleAttachmentObj(file);
    const tempImageObj = await formulateAttachmentPreview(file, file?.name, "");
    if (tempImageObj?.fileImagePreview === "") {
      const imageUrl = await readImageFileAsync(file);
      setSingleAttachmetSelectedImageUrl(imageUrl);
    }
  };

  /* open image preview for dialog */
  const handleAttachmentPreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await readImageFileAsync(file.originFileObj);
    }
    setPreviewImageSrc(file?.url || file?.preview);
    setOpenPreviewDialog(true);
    const tempPreviewTitle =
      file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
    setPreviewTitle(tempPreviewTitle);
  };
  /* open image preview for dialog */

  const onSubmit = (values) => {
    const formData = new FormData();
    attachmentList?.forEach((element) => {
      formData.append("images", element?.originFileObj);
    });

    formData.append("name", userSession?.userName);
    formData.append("email", userSession?.userEmail);
    formData.append("phone", loginDetails?.phone?.phone_number);
    formData.append("subject", values?.title);
    formData.append("message", values?.body);
    formData.append("documents", "");
    formData.append("videos", "");

    dispatch(
      createServiceProviderSupportTicketAction(formData, setLoading)
    ).then(() => {
      form.resetFields();
      setAttachmentList([]);
    });
  };

  return (
    <div className="helpAndSupportContainer">
      <Flex vertical gap="2rem">
        <Row justify="start">
          <Col span={12}>
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              requiredMark={false}
              onFinish={onSubmit}
              style={{ margin: "2rem" }}
            >
              <Flex vertical gap="2rem">
                <Card bordered>
                  <Form.Item
                    style={{ width: "100%" }}
                    name="title"
                    label={
                      <RequiredLabel
                        text={t("Modules.Notify.title")}
                        required={true}
                      />
                    }
                    rules={[
                      {
                        required: true,
                        message: t("Modules.Notify.pleaseEnterTitle"),
                      },
                      {
                        whitespace: true,
                        message: t("Modules.Notify.pleaseEnterTitle"),
                      },
                    ]}
                    normalize={(value) => sanitizeDataTypeClientSide(value)}
                  >
                    <Input placeholder={t("Modules.Notify.enterTitle")} />
                  </Form.Item>

                  <Form.Item
                    label={<RequiredLabel text={t("body")} required={true} />}
                    name="body"
                    rules={[
                      {
                        required: true,
                        message: t("Modules.Notify.bodyCannotBeEmpty"),
                      },
                      {
                        whitespace: true,
                        message: t("Modules.Notify.bodyCannotBeEmpty"),
                      },
                    ]}
                    normalize={(value) => sanitizeDataTypeClientSide(value)}
                  >
                    <TextArea
                      rows={4}
                      showCount
                      maxLength={
                        appConstConfig.DEFAULT_CHARACTERS_ALLOWED_FOR_TEXTAREA
                      }
                      placeholder={t("validations.body")}
                    />
                  </Form.Item>
                </Card>
              </Flex>

              <Flex gap="1rem" align="center" justify="center">
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ marginTop: "1rem" }}
                  loading={loading}
                  disabled={loading}
                >
                  {t("CommonUtils.submit")}
                </Button>
              </Flex>
            </Form>
          </Col>
          <Col span={7}>
            <Typography.Title level={4} style={{ marginBottom: 10 }}>
              <Flex gap="0.2rem" align="center">
                <RequiredLabel text={t("UploadImages")} required={false} />
                <Tooltip
                  placement="topLeft"
                  title={t("CommonUtils.multipleImagesTooltip")}
                  color={colors.DarkMint}
                >
                  <Button type="text" shape="circle" size="small">
                    <Flex align="center" justify="center">
                      <InfoButtonIcon />
                    </Flex>
                  </Button>
                </Tooltip>
              </Flex>
            </Typography.Title>

            <Flex
              style={{ flexDirection: "column-reverse", textAlign: "center" }}
            >
              <Flex vertical gap="0.5rem">
                {attachmentList?.length < 4 && (
                  <div
                    className="attachmentContainer"
                    style={{ cursor: "pointer" }}
                    onClick={() => attachmentRef.current?.click()}
                  >
                    <Button
                      type="primary"
                      shape="circle"
                      size="middle"
                      icon={<PlusOutlined style={{ fontSize: "22px" }} />}
                    />
                  </div>
                )}
                <Flex
                  vertical
                  style={{ width: "385px" }}
                  align="center"
                  justify="center"
                >
                  <Typography.Title
                    level={3}
                    type="secondary"
                    style={{ margin: 0 }}
                  >
                    {t("CommonUtils.supportedImageFormats")}
                  </Typography.Title>
                  <Typography.Title
                    level={3}
                    type="secondary"
                    style={{ margin: 0 }}
                  >
                    {t("maximumImagesAllowed", {
                      limit_number: 4,
                      type: "images",
                    })}
                  </Typography.Title>
                </Flex>
              </Flex>
              <Flex vertical align="center" justify="center">
                <Upload
                  listType="picture-card"
                  fileList={attachmentList}
                  accept="image/*"
                  multiple
                  maxCount={4}
                  onPreview={handleAttachmentPreview}
                  onChange={({ fileList }) => {
                    setAttachmentList(fileList);
                  }}
                  onRemove={(file) =>
                    setAttachmentList(
                      attachmentList.filter(
                        (element) => element.uid !== file.uid
                      )
                    )
                  }
                  customRequest={async (options) => {
                    setSingleAttachmentObj(options?.file);
                    const tempImageObj = await formulateAttachmentPreview(options?.file, options?.file?.name,"");
                    if (tempImageObj?.fileImagePreview === "") {
                      const imageUrl = await readImageFileAsync(options?.file);
                      setSingleAttachmetSelectedImageUrl(imageUrl);
                      options.file["url"] = imageUrl;
                      options.onSuccess(imageUrl, options.file);
                    }
                  }}
                  className="uploadContainer"
                >
                  <div ref={attachmentRef}></div>
                </Upload>
                <Modal
                  open={openPreviewDialog}
                  title={previewTitle}
                  footer={null}
                  onCancel={() => setOpenPreviewDialog(false)}
                >
                  <img style={{ width: "100%" }} src={previewImageSrc} />
                </Modal>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </Flex>
    </div>
  );
};

export default HelpAndSupport;
